import { httpClient } from '@/core/api/httpClient';
import type {
  OrganizationUnit,
  CreateOrganizationUnitPayload,
  UpdateOrganizationUnitPayload,
  OrgChartRootDto,
  OrgChartProjectsRootDto,
  MemberSummaryDto,
} from '../types';
import {
  organizationUnitsMock,
  orgChartMock,
  orgChartProjectsMock,
  memberSummaryMock,
} from './organization-units.mock';
import { env } from '@/core/config/env';
import { API_V1 } from '@/core/api/endpoints';

const USE_MOCK = env.VITE_USE_MOCK === 'true';

interface AreaDto {
  id: string;
  name: string;
  parentId: string | null;
  color: string;
  memberCount: number;
  createdAt: string;
}

/**
 * Converts the flat `AreaDto[]` from the API into `OrganizationUnit[]` with
 * client-computed `depth` and `order` fields.
 *
 * @remarks
 * The API returns units in a flat array — neither `depth` nor `order` is
 * provided by the server. `order` is assigned as a sequential counter per
 * `parentId` group (preserving API response order). `depth` is computed by
 * walking the parent chain with memoization to avoid redundant traversals on
 * deep or wide trees.
 */
function mapDtosToUnits(dtos: AreaDto[]): OrganizationUnit[] {
  // Assign order: sequential index within each parentId group (preserves API order)
  const orderCounters = new Map<string | null, number>();
  const withOrder = dtos.map((dto) => {
    const key = dto.parentId;
    const order = orderCounters.get(key) ?? 0;
    orderCounters.set(key, order + 1);
    return { ...dto, order, depth: 0 };
  });

  // Compute depth by walking the parent chain with memoization
  const unitMap = new Map(withOrder.map((u) => [u.id, u]));
  const depthCache = new Map<string, number>();

  const computeDepth = (id: string): number => {
    if (depthCache.has(id)) return depthCache.get(id)!;
    const unit = unitMap.get(id);
    if (!unit?.parentId) {
      depthCache.set(id, 0);
      return 0;
    }
    const depth = computeDepth(unit.parentId) + 1;
    depthCache.set(id, depth);
    return depth;
  };

  return withOrder.map((u) => ({ ...u, depth: computeDepth(u.id) }));
}

export const organizationUnitsService = {
  getAll: async (signal?: AbortSignal): Promise<OrganizationUnit[]> => {
    if (USE_MOCK) {
      return [...organizationUnitsMock];
    }

    const { data } = await httpClient.get<AreaDto[]>(`${API_V1}/tenants/areas`, { signal });
    return mapDtosToUnits(data);
  },

  create: async (payload: CreateOrganizationUnitPayload): Promise<OrganizationUnit> => {
    if (USE_MOCK) {
      const siblings = organizationUnitsMock.filter(
        (u) => u.parentId === (payload.parentId ?? null),
      );
      const order = siblings.length > 0 ? Math.max(...siblings.map((s) => s.order)) + 1 : 0;

      let depth = 0;
      if (payload.parentId) {
        const parent = organizationUnitsMock.find((u) => u.id === payload.parentId);
        depth = parent ? parent.depth + 1 : 0;
      }

      const newUnit: OrganizationUnit = {
        id: `unit-${Date.now()}`,
        name: payload.name,
        description: payload.description,
        icon: payload.icon,
        color: payload.color,
        parentId: payload.parentId ?? null,
        memberCount: 0,
        createdAt: new Date().toISOString(),
        order,
        depth,
      };
      organizationUnitsMock.push(newUnit);
      return newUnit;
    }

    // parentId is omitted when undefined so the backend treats the unit as a root node
    const apiBody = {
      name: payload.name,
      color: payload.color,
      icon: payload.icon,
      ...(payload.parentId !== undefined && { parentId: payload.parentId }),
    };
    const { data } = await httpClient.post<typeof apiBody, AreaDto>(
      `${API_V1}/tenants/areas`,
      apiBody,
    );
    // depth/order are recomputed on the next getAll; set temporary defaults
    return { ...data, depth: 0, order: 0 };
  },

  update: async (
    unitId: string,
    payload: UpdateOrganizationUnitPayload,
  ): Promise<OrganizationUnit> => {
    if (USE_MOCK) {
      const index = organizationUnitsMock.findIndex((u) => u.id === unitId);
      if (index === -1) throw new Error('Organization unit not found');
      const updated = { ...organizationUnitsMock[index], ...payload };
      organizationUnitsMock[index] = updated;
      return updated;
    }

    const apiBody = {
      name: payload.name,
      parentId: payload.parentId,
      color: payload.color,
      icon: payload.icon,
    };
    const { data } = await httpClient.put<typeof apiBody, AreaDto>(
      `${API_V1}/tenants/areas/${unitId}`,
      apiBody,
    );
    return { ...data, depth: 0, order: 0 };
  },

  delete: async (unitId: string): Promise<void> => {
    if (USE_MOCK) {
      // Remove direct children first (API handles cascading on real server)
      const childIndices = organizationUnitsMock
        .map((u, i) => (u.parentId === unitId ? i : -1))
        .filter((i) => i !== -1);

      for (let i = childIndices.length - 1; i >= 0; i--) {
        organizationUnitsMock.splice(childIndices[i], 1);
      }

      const index = organizationUnitsMock.findIndex((u) => u.id === unitId);
      if (index !== -1) organizationUnitsMock.splice(index, 1);
      return;
    }

    await httpClient.delete(`${API_V1}/tenants/areas/${unitId}`);
  },

  getOrgChart: async (signal?: AbortSignal): Promise<OrgChartRootDto> => {
    if (USE_MOCK) return orgChartMock;
    const { data } = await httpClient.get<OrgChartRootDto>(`${API_V1}/tenants/org-chart`, {
      signal,
    });
    return data;
  },

  getOrgChartProjects: async (signal?: AbortSignal): Promise<OrgChartProjectsRootDto> => {
    if (USE_MOCK) return orgChartProjectsMock;
    const { data } = await httpClient.get<OrgChartProjectsRootDto>(
      `${API_V1}/tenants/org-chart/projects`,
      { signal },
    );
    return data;
  },

  getMemberSummary: async (memberId: string, signal?: AbortSignal): Promise<MemberSummaryDto> => {
    if (USE_MOCK) return memberSummaryMock(memberId);
    const { data } = await httpClient.get<MemberSummaryDto>(
      `${API_V1}/tenants/org-chart/members/${memberId}/summary`,
      { signal },
    );
    return data;
  },
};
